import { Theme } from '@mui/material';
import { getCookie } from '../../common/utils/cookies';
import { BrandColors } from '../../constants';
import { TemplateLayout } from '../../store/interfaces/template';
import { isJourneyTheme } from '../../common/utils/fallbackCondition';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  layoutCode?: TemplateLayout;
  isICPEnabled?: boolean;
}

export const simplifiedAuthStyles = makeStyles<StyleProps | void>()((theme: Theme, props) => {
  const isBasicFlow = getCookie('BasicFlow') === '1';
  return {
    container: {
      marginLeft: 'auto',
      marginRight: 'auto',
      [theme.breakpoints.up('lg')]: {
        maxWidth: '960px',
      },
    },
    grid: {
      width: '100%',
      minHeight: 'calc(100vh - 40px - 165px)',
      [theme.breakpoints.up('lg')]: {
        padding: '2rem 0',
      },
      [theme.breakpoints.up('xl')]: {
        padding: '5rem 0',
      },
    },
    fullHeight: {
      height: '100%',
    },
    signUpContainer: {
      padding: '1.5rem',
      paddingBottom: props?.isICPEnabled ? '2.5rem' : '1.5rem',
      [theme.breakpoints.up('md')]: {
        padding: isJourneyTheme(props?.layoutCode) ? '1.5rem' : '4rem 10rem',
      },
      [theme.breakpoints.up('lg')]: {
        padding: '2rem',
        borderLeft: `${isBasicFlow ? '1px' : '0px'} solid ${theme.palette?.borderColor?.main}`,
      },
      '& .MuiInputBase-input': {
        fontSize: '14px',

        // to hide show password icon (provided as default by some browsers)
        '&::-ms-reveal': {
          display: 'none',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.borderColor?.main,
      },
    },
    welcomeContentContainer: {
      width: '100%',
      padding: '1.5rem',
      wordBreak: 'break-word',
      [theme.breakpoints.up('md')]: {
        padding: '2rem 10rem',
      },
      [theme.breakpoints.up('lg')]: {
        padding: '2rem',
      },
    },
    marginBottom15: {
      marginBottom: '15px !important',
    },
    marginTopBottom15: {
      margin: '15px 0px',
    },
    paddingTopBottom15: {
      padding: '15px 0px',
    },
    marginBottom5: {
      marginBottom: '5px',
    },
    socialSignOnLabel: {
      display: 'flex',
      alignItems: 'center',
      '& .line': {
        height: '1px',
        flex: 1,
        backgroundColor: theme.palette.borderColor?.main,
      },
      '& h6': {
        padding: '0 0.5rem',
        fontWeight: 600,
      },
    },
    ssoOption: {
      cursor: 'pointer',
      fontSize: '12px',
      borderRadius: '50px',
      width: '3.75rem',
      height: '3.75rem',
      boxShadow: `2px 2px 8px ${BrandColors.blackWithOpacity1}`,
      marginBottom: '.5rem',
      [theme.breakpoints.up('md')]: {
        border: `1px solid ${theme.palette.basicTaskListIcons?.lockAndCompleted.color}`,
        height: '2.5rem',
        width: 'auto',
        padding: '8px 14px',
        borderRadius: '5px',
        boxShadow: `1px 1px 2px ${BrandColors.blackWithOpacity08}`,
      },
    },
    helperText: {
      fontSize: '12px !important',
      color: theme.palette.error.main,
      display: 'flex',
      lineHeight: '18px',
      '& .MuiSvgIcon-root': {
        color: theme.palette.error.main,
        width: '1rem',
        height: '1rem',
      },
    },
    passwordValidation: {
      fontSize: '12px',
      display: 'flex',
      alignItems: 'baseline',
      '& .MuiSvgIcon-root': {
        width: '10px',
        height: '10px',
        margin: '2px',
      },
    },
    passwordMsgList: {
      fontSize: '12px',
      margin: 0,
      paddingLeft: '19px',
    },
    errorText: {
      color: `${theme.palette.error.main} !important`,
    },
    successText: {
      color: `${theme.palette.success.main} !important`,
    },
    fontSize14: {
      fontSize: '14px !important',
    },
    buttonProgress: {
      color: theme.palette.primary.main,
      position: 'absolute',
      top: '20%',
      left: '46.75%',
    },
    registerBtn: {
      fontSize: '12px',
    },
    errorAlert: {
      fontSize: '12px',
    },
    pointerCursor: {
      cursor: 'pointer',
    },
    dpnLabel: {
      '& .MuiCheckbox-root': {
        marginLeft: '-12px',
      },
    },
    dpnLabelItem: {
      '&:not(:last-child)': {
        marginBottom: '0.75rem',
      },
    },
    fontWeight600: {
      fontWeight: 600,
    },
    createPasswordBox: {
      padding: '1.5rem',
      [theme.breakpoints.up('md')]: {
        padding: isJourneyTheme(props?.layoutCode) ? '1.5rem' : '4rem 10rem',
      },
      [theme.breakpoints.up('lg')]: {
        padding: '2rem',
      },
    },
    newPasswordLabel: {
      lineHeight: '24px',
    },
    marginBottom30: {
      marginBottom: '30px',
    },
    createPasswordInput: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.borderColor?.main,
      },
      '& .MuiInputBase-input': {
        border: 'none',
      },
    },
    buttonFullWidth: {
      marginTop: '15px',
      width: '100%',
    },
    forgotPasswordMsg: {
      '& .MuiGrid-item': {
        display: 'grid',
      },
      '& .marginBottom10': {
        marginBottom: '10px',
      },
      '& .infoIcon': {
        margin: '0px 15px 0px 5px',
      },
      marginTop: '1rem',
    },
    signOut: {
      marginBottom: '80px',
      marginTop: '80px',
    },
    customError: {
      outline: 'none',
      fontSize: '12px !important',
      color: theme.palette.error.main,
      display: 'flex',
      '& .MuiSvgIcon-root': {
        marginTop: '5px',
        color: theme.palette.error.main,
        width: '1rem',
        height: '1rem',
      },
    },
    cookieConsent: {
      '& .MuiLink-root': {
        fontSize: '14px',
      },
    },
    autoLogin: {
      margin: '5rem 2rem',
      width: '100%',
      [theme.breakpoints.down('md')]: {
        margin: '5rem 1rem',

        '& button': {
          width: '100%',
        },
      },
      [theme.breakpoints.down('lg')]: {
        '& label': {
          '& .MuiFormControlLabel-label': {
            textAlign: 'left',
          },
        },
      },
    },
    customSuccess: {
      fontSize: '12px !important',
      color: theme.palette.success.main,
      display: 'flex',
      '& .MuiSvgIcon-root': {
        marginTop: '5px',
        color: theme.palette.success.main,
        width: '1rem',
        height: '1rem',
      },
    },
    greyBackground: {
      backgroundColor: isBasicFlow ? theme.palette.alert?.outlinedInfo.background : theme.palette.background.paper,
    },
    welcomeContentGrid: {
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    alertIcon: {
      '& .colorChange': {
        stroke: theme.palette.text.primary,
      },
    },
  };
});
